import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  Copy,
  Download,
  FilterX,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { DocumentLifecycleRail } from './DocumentLifecycleRail';
import { EQMS_STATUSES, type ControlledDocument, type EqmsStatus } from '../../types/eqms.types';

type SortKey = 'controlCode' | 'name' | 'status' | 'department' | 'ownerName' | 'updatedAt';

interface ColumnDef {
  key: SortKey | 'version' | 'lifecycle' | 'actions';
  label: string;
  sortKey?: SortKey;
  filterType?: 'text' | 'select' | 'date-range';
  defaultVisible?: boolean;
  noFilter?: boolean;
  getValue: (document: ControlledDocument) => string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'controlCode', label: 'Control ID', sortKey: 'controlCode', filterType: 'text', getValue: (doc) => doc.controlCode },
  { key: 'name', label: 'Document', sortKey: 'name', filterType: 'text', getValue: (doc) => doc.name },
  { key: 'version', label: 'Version', getValue: (doc) => doc.controlledVersion },
  { key: 'status', label: 'Status', sortKey: 'status', filterType: 'select', getValue: (doc) => doc.lifecycleStatus },
  { key: 'lifecycle', label: 'Lifecycle', defaultVisible: false, getValue: (doc) => doc.lifecycleStatus },
  { key: 'department', label: 'Department', sortKey: 'department', filterType: 'text', getValue: (doc) => doc.department },
  { key: 'ownerName', label: 'Owner', sortKey: 'ownerName', filterType: 'text', getValue: (doc) => doc.ownerName },
  { key: 'updatedAt', label: 'Last change', sortKey: 'updatedAt', filterType: 'date-range', getValue: (doc) => doc.updatedAt },
  { key: 'actions', label: '', noFilter: true, getValue: () => '' },
];

const STATUS_LABELS: Record<EqmsStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  effective: 'Effective',
  obsolete: 'Obsolete',
  discarded: 'Discarded',
};

const STATUS_STYLES: Record<EqmsStatus, string> = {
  draft: 'border-slate-200 bg-slate-100 text-slate-700',
  in_review: 'border-amber-200 bg-amber-50 text-amber-800',
  approved: 'border-blue-200 bg-blue-50 text-blue-800',
  effective: 'border-teal-200 bg-teal-50 text-teal-800',
  obsolete: 'border-slate-300 bg-slate-200 text-slate-700',
  discarded: 'border-red-200 bg-red-50 text-red-700 line-through',
};

const VISIBILITY_STORAGE_KEY = 'atek-eqms-column-visibility';

function defaultVisibility() {
  return Object.fromEntries(COLUMNS.map((column) => [column.key, column.defaultVisible !== false]));
}

function loadVisibility() {
  try {
    return { ...defaultVisibility(), ...JSON.parse(localStorage.getItem(VISIBILITY_STORAGE_KEY) || '{}') };
  } catch {
    return defaultVisibility();
  }
}

function escapeCsv(value: unknown) {
  let text = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function exportCsv(items: ControlledDocument[], filename: string) {
  const columns = COLUMNS.filter((column) => !column.noFilter);
  const rows = [
    columns.map((column) => escapeCsv(column.label)).join(','),
    ...items.map((item) => columns.map((column) => escapeCsv(column.getValue(item))).join(',')),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function applyColumnFilters(
  documents: ControlledDocument[],
  columnFilters: Record<string, string>,
) {
  const activeFilters = Object.entries(columnFilters).filter(([, value]) => value);
  if (!activeFilters.length) return documents;
  return documents.filter((document) => activeFilters.every(([key, value]) => {
    const column = COLUMNS.find((candidate) => candidate.key === key);
    if (!column || column.noFilter) return true;
    const raw = column.getValue(document);
    if (column.filterType === 'select') {
      try {
        const selected = JSON.parse(value) as string[];
        return !selected.length || selected.includes(raw);
      } catch {
        return true;
      }
    }
    if (column.filterType === 'date-range') {
      const [from, to] = value.split('>>');
      if (from && raw < from) return false;
      if (to && raw > `${to}T23:59:59`) return false;
      return true;
    }
    return raw.toLowerCase().includes(value.toLowerCase());
  }));
}

interface ControlledDocumentsTableProps {
  documents: ControlledDocument[];
  total: number;
  page: number;
  pageLength: number;
  sortKey: SortKey;
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  isLoading: boolean;
  isRefreshing: boolean;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters: () => void;
  onSort: (key: SortKey) => void;
  onPageChange: (page: number) => void;
  onRowClick: (document: ControlledDocument) => void;
  onRefresh: () => void;
  onExportAll: () => Promise<ControlledDocument[]>;
}

export function ControlledDocumentsTable({
  documents,
  total,
  page,
  pageLength,
  sortKey,
  sortDirection,
  columnFilters,
  isLoading,
  isRefreshing,
  onColumnFilterChange,
  onClearColumnFilters,
  onSort,
  onPageChange,
  onRowClick,
  onRefresh,
  onExportAll,
}: ControlledDocumentsTableProps) {
  const { t } = useTranslation('qms');
  const [visibility, setVisibility] = useState<Record<string, boolean>>(loadVisibility);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const visibleColumns = COLUMNS.filter((column) => visibility[column.key] !== false);
  const hasFilters = Object.values(columnFilters).some(Boolean);

  const filteredDocuments = useMemo(
    () => applyColumnFilters(documents, columnFilters),
    [columnFilters, documents],
  );

  const toggleColumn = (key: string) => {
    const next = { ...visibility, [key]: visibility[key] === false };
    setVisibility(next);
    localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(next));
  };

  const copyPage = async () => {
    try {
      const columns = COLUMNS.filter((column) => !column.noFilter);
      const value = [
        columns.map((column) => column.label).join('\t'),
        ...filteredDocuments.map((document) => columns.map((column) => column.getValue(document)).join('\t')),
      ].join('\n');
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${filteredDocuments.length} controlled documents`);
    } catch {
      toast.error('Could not copy controlled documents');
    }
  };

  const exportAll = async () => {
    try {
      setIsExportingAll(true);
      const allDocuments = await onExportAll();
      const filteredAllDocuments = applyColumnFilters(allDocuments, columnFilters);
      exportCsv(filteredAllDocuments, 'eqms-controlled-documents-all');
      toast.success(`Exported ${filteredAllDocuments.length} controlled documents`);
    } catch {
      toast.error('Could not export all controlled documents');
    } finally {
      setIsExportingAll(false);
    }
  };

  const renderFilter = (column: ColumnDef) => {
    if (column.noFilter || !column.filterType) return <TableHead key={`filter-${column.key}`} />;
    if (column.filterType === 'select') {
      let selected: string[] = [];
      try {
        selected = columnFilters[column.key] ? JSON.parse(columnFilters[column.key]) : [];
      } catch {
        selected = [];
      }
      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 w-full justify-start text-xs font-normal">
                {selected.length ? `${selected.length} selected` : '—'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {EQMS_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selected.includes(status)}
                  onCheckedChange={() => {
                    const next = selected.includes(status)
                      ? selected.filter((value) => value !== status)
                      : [...selected, status];
                    onColumnFilterChange(column.key, next.length ? JSON.stringify(next) : '');
                  }}
                >
                  {STATUS_LABELS[status]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableHead>
      );
    }
    if (column.filterType === 'date-range') {
      const [from = '', to = ''] = (columnFilters[column.key] || '').split('>>');
      return (
        <TableHead key={`filter-${column.key}`} className="p-1">
          <div className="flex min-w-28 flex-col gap-0.5">
            <Input
              type="date"
              value={from}
              onChange={(event) => onColumnFilterChange(column.key, `${event.target.value}>>${to}`)}
              className="h-6 px-1 text-xs"
              title="From"
            />
            <Input
              type="date"
              value={to}
              onChange={(event) => onColumnFilterChange(column.key, `${from}>>${event.target.value}`)}
              className="h-6 px-1 text-xs"
              title="To"
            />
          </div>
        </TableHead>
      );
    }
    return (
      <TableHead key={`filter-${column.key}`} className="p-1">
        <Input
          value={columnFilters[column.key] || ''}
          onChange={(event) => onColumnFilterChange(column.key, event.target.value)}
          className="h-7 text-xs"
          placeholder="…"
          aria-label={`Filter ${column.label}`}
        />
      </TableHead>
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / pageLength));

  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-white">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-base">Controlled document register</CardTitle>
            <CardDescription>
              {filteredDocuments.length < documents.length
                ? `Showing ${filteredDocuments.length} of ${documents.length} on this page`
                : `Showing ${documents.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
                <FilterX className="mr-1 h-4 w-4" /> Clear filters
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={copyPage}>
              <Copy className="mr-1 h-4 w-4" /> Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="mr-1 h-4 w-4" /> {isExportingAll ? 'Exporting…' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  exportCsv(filteredDocuments, 'eqms-controlled-documents-page');
                  toast.success(`Exported ${filteredDocuments.length} controlled documents`);
                }}>
                  Export page ({filteredDocuments.length})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportAll}>Export all ({total})</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={cn('mr-1 h-4 w-4', isRefreshing && 'animate-spin')} /> Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="mr-1 h-4 w-4" /> Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.filter((column) => !column.noFilter).map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={visibility[column.key] !== false}
                    onCheckedChange={() => toggleColumn(column.key)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                {visibleColumns.map(renderFilter)}
              </TableRow>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableHead key={column.key}>
                    {column.sortKey ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSort(column.sortKey!)}
                        className="-ml-3 h-8 hover:bg-transparent"
                      >
                        {column.label}
                        {sortKey !== column.sortKey
                          ? <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />
                          : sortDirection === 1
                            ? <ArrowUp className="ml-1 h-3 w-3" />
                            : <ArrowDown className="ml-1 h-3 w-3" />}
                      </Button>
                    ) : column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 8 }).map((_, row) => (
                <TableRow key={row}>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!isLoading && filteredDocuments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-14 text-center text-sm text-muted-foreground">
                    No controlled documents match these filters.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filteredDocuments.map((document) => (
                <TableRow
                  key={document.id}
                  onClick={() => onRowClick(document)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key} className="text-sm">
                      {column.key === 'controlCode' && (
                        <span className="font-mono text-xs font-semibold text-slate-900">{document.controlCode}</span>
                      )}
                      {column.key === 'name' && (
                        <div>
                          <div className="max-w-xs truncate font-medium text-slate-900">{document.name}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{document.currentVersion?.originalName}</div>
                          {Boolean(document.pendingUpdateRequests) && (
                            <Badge variant="outline" className="mt-1 border-amber-200 bg-amber-50 text-[11px] text-amber-800">
                              {t('updateRequests.badge', { count: document.pendingUpdateRequests })}
                            </Badge>
                          )}
                        </div>
                      )}
                      {column.key === 'version' && <span className="font-mono text-xs">v{document.controlledVersion}</span>}
                      {column.key === 'status' && (
                        <Badge variant="outline" className={STATUS_STYLES[document.lifecycleStatus]}>
                          {STATUS_LABELS[document.lifecycleStatus]}
                        </Badge>
                      )}
                      {column.key === 'lifecycle' && (
                        <div className="min-w-40"><DocumentLifecycleRail status={document.lifecycleStatus} compact /></div>
                      )}
                      {column.key === 'department' && document.department}
                      {column.key === 'ownerName' && document.ownerName}
                      {column.key === 'updatedAt' && (
                        <span className="whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(document.updatedAt).toLocaleString()}
                        </span>
                      )}
                      {column.key === 'actions' && <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t px-4 py-3">
          <p className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 0}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
